import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import SearchHeader from './components/SearchHeader';
import SearchFilters from './components/SearchFilters';
import SearchResultsList from './components/SearchResultsList';
import SavedSearches from './components/SavedSearches';
import Button from '../../components/ui/Button';
import { documentService } from '../../services/documentService';

const SearchResults = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    documentType: 'all',
    documentBase: 'all',
    author: 'all',
    dateRange: { start: '', end: '' },
    categories: []
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedResults, setSelectedResults] = useState([]);

  // Real search results from database
  const [searchResults, setSearchResults] = useState([]);
  const [documentBases, setDocumentBases] = useState([]);

  // Load document bases for filter options
  useEffect(() => {
    const loadDocumentBases = async () => {
      try {
        const bases = await documentService?.getDocumentBases();
        setDocumentBases(bases);
      } catch (err) {
        console.error('Error loading document bases:', err);
      }
    };

    loadDocumentBases();
  }, []);

  // Get search query from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const query = urlParams?.get('q');
    if (query) {
      const decodedQuery = decodeURIComponent(query);
      setSearchQuery(decodedQuery);
      performSearch(decodedQuery);
    }
  }, [location?.search]);

  const performSearch = async (query) => {
    if (!query?.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Use the document service to search for real documents
      const results = await documentService?.searchDocuments(query?.trim());
      
      // Transform the results to match the expected format
      const transformedResults = results?.map((doc) => ({
        id: doc?.id,
        title: doc?.name,
        snippet: doc?.description || `Document: ${doc?.name}`,
        fullPreview: doc?.description || `This is the document: ${doc?.name}. File type: ${doc?.type || 'Unknown'}. Size: ${formatFileSize(doc?.fileSize)}. Last modified: ${formatDate(doc?.updatedAt)}.`,
        documentBase: doc?.documentBaseName || 'Unknown',
        author: doc?.ownerName || 'Unknown',
        lastModified: doc?.updatedAt,
        fileType: getFileExtension(doc?.name),
        fileSize: doc?.fileSize,
        version: doc?.version?.toString() || '1',
        relevanceScore: calculateRelevanceScore(query, doc),
        thumbnail: getDocumentThumbnail(doc?.mimeType),
        tags: generateTags(doc),
        keyPoints: generateKeyPoints(doc),
        // Additional fields for compatibility
        type: doc?.type || getFileTypeFromName(doc?.name),
        mimeType: doc?.mimeType,
        filePath: doc?.filePath,
        documentBaseId: doc?.documentBaseId,
        folderId: doc?.folderId,
        ownerId: doc?.ownerId,
        status: doc?.status,
        createdAt: doc?.createdAt
      })) || [];

      setSearchResults(transformedResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search documents. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString)?.toLocaleDateString();
  };

  const getFileExtension = (filename) => {
    if (!filename) return 'file';
    return filename?.split('.')?.pop()?.toLowerCase() || 'file';
  };

  const getFileTypeFromName = (filename) => {
    const extension = getFileExtension(filename);
    const typeMap = {
      'pdf': 'PDF',
      'doc': 'DOC',
      'docx': 'DOCX',
      'xls': 'XLS',
      'xlsx': 'XLSX',
      'ppt': 'PPT',
      'pptx': 'PPTX',
      'txt': 'TXT',
      'csv': 'CSV',
      'jpg': 'JPG',
      'jpeg': 'JPG',
      'png': 'PNG',
      'gif': 'GIF',
      'zip': 'ZIP'
    };
    return typeMap?.[extension] || extension?.toUpperCase();
  };

  const calculateRelevanceScore = (query, doc) => {
    if (!query || !doc) return 50;
    
    const queryLower = query?.toLowerCase();
    const nameLower = doc?.name?.toLowerCase() || '';
    const descLower = doc?.description?.toLowerCase() || '';
    
    let score = 0;
    
    // Exact matches get highest score
    if (nameLower?.includes(queryLower)) score += 40;
    if (descLower?.includes(queryLower)) score += 20;
    
    // Partial word matches
    const queryWords = queryLower?.split(' ');
    queryWords?.forEach(word => {
      if (nameLower?.includes(word)) score += 10;
      if (descLower?.includes(word)) score += 5;
    });
    
    // Recent documents get slight boost
    const daysSinceUpdate = (Date.now() - new Date(doc?.updatedAt || 0)) / (1000 * 60 * 60 * 24);
    if (daysSinceUpdate < 7) score += 10;
    else if (daysSinceUpdate < 30) score += 5;
    
    return Math.min(Math.max(score, 10), 100); // Keep between 10-100
  };

  const getDocumentThumbnail = (mimeType) => {
    // Return appropriate thumbnail based on MIME type
    const typeMap = {
      'application/pdf': 'https://images.unsplash.com/photo-1568124924590-969ea0925fcc?w=400',
      'application/msword': 'https://images.unsplash.com/photo-1586436008908-49c3fb035022?w=400',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'https://images.unsplash.com/photo-1586436008908-49c3fb035022?w=400',
      'application/vnd.ms-excel': 'https://images.unsplash.com/photo-1650204767806-84512c9f9417?w=400',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'https://images.unsplash.com/photo-1650204767806-84512c9f9417?w=400',
      'text/plain': 'https://images.unsplash.com/photo-1593041574730-c184025eb897?w=400'
    };
    
    return typeMap?.[mimeType] || 'https://images.unsplash.com/photo-1568124924590-969ea0925fcc?w=400';
  };

  const generateTags = (doc) => {
    const tags = [];
    
    // Add file type
    if (doc?.type) tags?.push(doc?.type?.toLowerCase());
    
    // Add document base name as tag
    if (doc?.documentBaseName) {
      tags?.push(doc?.documentBaseName?.toLowerCase()?.replace(/\s+/g, '-'));
    }
    
    // Add generic tags based on content
    const name = doc?.name?.toLowerCase() || '';
    if (name?.includes('policy') || name?.includes('procedure')) tags?.push('policy');
    if (name?.includes('manual') || name?.includes('guide')) tags?.push('manual');
    if (name?.includes('report') || name?.includes('analysis')) tags?.push('report');
    if (name?.includes('security') || name?.includes('safety')) tags?.push('security');
    
    return tags?.slice(0, 4); // Limit to 4 tags
  };

  const generateKeyPoints = (doc) => {
    const keyPoints = [];
    
    // Generate key points based on available data
    if (doc?.version && parseInt(doc?.version) > 1) {
      keyPoints?.push(`Version ${doc?.version} - Updated document`);
    }
    
    if (doc?.fileSize) {
      keyPoints?.push(`File size: ${formatFileSize(doc?.fileSize)}`);
    }
    
    if (doc?.updatedAt) {
      const updateDate = new Date(doc.updatedAt);
      const daysAgo = Math.floor((Date.now() - updateDate?.getTime()) / (1000 * 60 * 60 * 24));
      if (daysAgo < 7) {
        keyPoints?.push('Recently updated');
      }
    }
    
    if (doc?.ownerName) {
      keyPoints?.push(`Owned by ${doc?.ownerName}`);
    }
    
    return keyPoints?.slice(0, 4); // Limit to 4 key points
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      performSearch(searchQuery);
      // Update URL with search query
      const newUrl = `/search-results?q=${encodeURIComponent(searchQuery)}`;
      window.history?.pushState({}, '', newUrl);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Re-apply search with new filters
    if (searchQuery?.trim()) {
      performSearch(searchQuery);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      documentType: 'all',
      documentBase: 'all',
      author: 'all',
      dateRange: { start: '', end: '' },
      categories: []
    });
    // Re-apply search without filters
    if (searchQuery?.trim()) {
      performSearch(searchQuery);
    }
  };

  const handleSaveSearch = (name) => {
    // In a real app, this would save the search to the backend
    console.log('Saving search:', { name, query: searchQuery, filters });
  };

  const handleExportResults = () => {
    // In a real app, this would export the search results
    console.log('Exporting results for query:', searchQuery);
  };

  const handleBulkAction = (action, resultIds) => {
    console.log('Bulk action:', action, 'on results:', resultIds);

    switch (action) {
      case 'download':
        // Handle bulk download
        break;
      case 'analyze':
        window.location.href = '/ai-analysis-dashboard';
        break;
      case 'export':
        // Handle metadata export
        break;
      case 'add-to-collection':
        // Handle adding to collection
        break;
      case 'load-more':
        // Handle loading more results
        break;
      default:
        break;
    }
  };

  const handleNavigate = (action, result) => {
    switch (action) {
      case 'view':
        window.location.href = `/document-editor?id=${result?.id}&mode=view`;
        break;
      case 'edit':
        window.location.href = `/document-editor?id=${result?.id}&mode=edit`;
        break;
      case 'analyze':
        window.location.href = `/ai-analysis-dashboard?document=${result?.id}`;
        break;
      case 'download':
        // Handle download using document service
        if (result?.filePath) {
          documentService?.getDocumentUrl(result?.filePath)?.then(url => {
              const link = document.createElement('a');
              link.href = url;
              link.download = result?.title || result?.name;
              link?.click();
            })?.catch(err => console.error('Download failed:', err));
        }
        break;
      default:
        break;
    }
  };

  const handleSavedSearchSelect = (query) => {
    setSearchQuery(query);
    performSearch(query);
  };

  // Filter results based on current filters
  const filteredResults = searchResults?.filter((result) => {
    if (filters?.documentType !== 'all') {
      const typeMatch = result?.type?.toLowerCase() === filters?.documentType?.toLowerCase() ||
                       result?.tags?.some((tag) => tag?.toLowerCase()?.includes(filters?.documentType?.toLowerCase()));
      if (!typeMatch) return false;
    }

    if (filters?.documentBase !== 'all') {
      const baseMatch = result?.documentBase?.toLowerCase()?.includes(filters?.documentBase?.toLowerCase()?.replace('-', ' '));
      if (!baseMatch) return false;
    }

    if (filters?.author !== 'all') {
      const authorMatch = result?.author?.toLowerCase()?.includes(filters?.author?.toLowerCase()?.replace('-', ' '));
      if (!authorMatch) return false;
    }

    if (filters?.dateRange?.start || filters?.dateRange?.end) {
      const resultDate = new Date(result?.lastModified);
      if (filters?.dateRange?.start && resultDate < new Date(filters.dateRange.start)) return false;
      if (filters?.dateRange?.end && resultDate > new Date(filters.dateRange.end)) return false;
    }

    if (filters?.categories?.length > 0) {
      const categoryMatch = filters?.categories?.some((category) =>
        result?.tags?.includes(category)
      );
      if (!categoryMatch) return false;
    }

    return true;
  }) || [];

  // Sort results
  const sortedResults = [...filteredResults]?.sort((a, b) => {
    switch (sortBy) {
      case 'relevance':
        return (b?.relevanceScore || 0) - (a?.relevanceScore || 0);
      case 'date-desc':
        return new Date(b.lastModified || 0) - new Date(a.lastModified || 0);
      case 'date-asc':
        return new Date(a.lastModified || 0) - new Date(b.lastModified || 0);
      case 'title-asc':
        return (a?.title || '')?.localeCompare(b?.title || '');
      case 'title-desc':
        return (b?.title || '')?.localeCompare(a?.title || '');
      case 'size-desc':
        return (b?.fileSize || 0) - (a?.fileSize || 0);
      case 'size-asc':
        return (a?.fileSize || 0) - (b?.fileSize || 0);
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        isMenuOpen={sidebarOpen}
      />

      <div className="flex pt-16">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <div className="flex-1 lg:ml-60">
          <SearchHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearch={handleSearch}
            totalResults={sortedResults?.length || 0}
            searchTime={loading ? null : 245}
            onSaveSearch={handleSaveSearch}
            onExportResults={handleExportResults}
          />

          {error && (
            <div className="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="flex">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden fixed bottom-6 right-6 z-50">
              <Button
                variant="default"
                size="icon"
                onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                iconName="Filter"
                iconSize={20}
                className="rounded-full shadow-lg"
              />
            </div>

            <SearchFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
              isCollapsed={filtersCollapsed}
              onToggleCollapse={() => setFiltersCollapsed(!filtersCollapsed)}
              resultCount={sortedResults?.length || 0}
              documentBases={documentBases}
            />

            <SearchResultsList
              results={sortedResults}
              searchQuery={searchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              selectedResults={selectedResults}
              onSelectionChange={setSelectedResults}
              onBulkAction={handleBulkAction}
              onNavigate={handleNavigate}
              loading={loading}
            />
          </div>

          {/* Floating Action Button for Saved Searches */}
          <div className="fixed bottom-6 left-6 z-50">
            <Button
              variant="outline"
              onClick={() => setShowSavedSearches(true)}
              iconName="Bookmark"
              iconSize={20}
              className="rounded-full shadow-lg bg-card"
            >
              Saved
            </Button>
          </div>
        </div>
      </div>

      {/* Saved Searches Modal */}
      {showSavedSearches && (
        <SavedSearches
          onSearchSelect={handleSavedSearchSelect}
          onClose={() => setShowSavedSearches(false)}
        />
      )}
    </div>
  );
};

export default SearchResults;